import {useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Layers, Network, Plus} from "lucide-react";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {mapApiError} from "../../shared/api/errors";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {
    activateReferralPolicyVersion,
    createReferralPolicyVersion,
    listReferralPolicies,
    referralPolicyKeys,
} from "./api";
import {
    canActivate,
    emptyPolicyRules,
    groupPolicyVersions,
    POLICY_LEVELS,
    ROUNDING_MODES,
    SCOPE_TYPES,
    scopeType,
    validatePolicyDraft,
} from "./contracts";
import "./referralPolicies.css";
import {useTranslation, i18n} from "../../shared/i18n";


const STATUS_TONE = {active: "success", draft: "neutral", retired: "danger"};
const STATUS_LABEL = {get active() { return i18n.t("sections.referralPolicies.p1"); }, get draft() { return i18n.t("sections.referralPolicies.p2"); }, get retired() { return i18n.t("sections.referralPolicies.p3"); }};

const ROUNDING_LABEL = {
    get floor() { return i18n.t("sections.referralPolicies.p4"); },
    get ceil() { return i18n.t("sections.referralPolicies.p5"); },
    get half_up() { return i18n.t("sections.referralPolicies.p6"); },
};

const emptyDraft = () => ({
    name: "",
    scopeType: "global",
    targetId: "",
    role: "",
    effectiveFrom: "",
    roundingMode: "floor",
    rules: emptyPolicyRules(),
});

const PolicyVersionForm = ({policyId, tenantId, pending, onCancel, onSubmit}) => {
    const {t} = useTranslation();
    const [draft, setDraft] = useState(emptyDraft);
    const [attempted, setAttempted] = useState(false);
    const errors = validatePolicyDraft(draft);
    const definition = scopeType(draft.scopeType);
    const showError = (field) => (attempted ? errors[field] : undefined);

    const setField = (field, value) => setDraft((current) => ({...current, [field]: value}));
    const setRule = (level, patch) => setDraft((current) => ({
        ...current,
        rules: current.rules.map((rule) => (rule.level === level ? {...rule, ...patch} : rule)),
    }));

    const submit = (event) => {
        event.preventDefault();
        setAttempted(true);
        if (Object.keys(errors).length) return;
        onSubmit({...draft, tenantId, policyId});
    };

    return (
        <form className="admin-card referral-policy-form" onSubmit={submit}>
            <h2>{policyId ? t("sections.referralPolicies.p7") : t("sections.referralPolicies.p8")}</h2>
            <p className="admin-card-note">
                {t("sections.referralPolicies.p9")}
            </p>

            <div className="referral-policy-fields">
                <label className="admin-field">
                    <span>{t("sections.referralPolicies.p10")}</span>
                    <input
                        value={draft.name}
                        disabled={pending}
                        onChange={(event) => setField("name", event.target.value)}
                    />
                    {showError("name") ? <small role="alert">{showError("name")}</small> : null}
                </label>

                <label className="admin-field">
                    <span>{t("sections.referralPolicies.p11")}</span>
                    <select
                        aria-label={t("sections.referralPolicies.p11")}
                        value={draft.scopeType}
                        disabled={pending}
                        onChange={(event) => setField("scopeType", event.target.value)}
                    >
                        {SCOPE_TYPES.map((scope) => (
                            <option key={scope.value} value={scope.value}>{scope.label}</option>
                        ))}
                    </select>
                    <small>{definition?.hint}</small>
                </label>

                {definition?.target === "id" ? (
                    <label className="admin-field">
                        <span>{t("sections.referralPolicies.p12")}</span>
                        <input
                            value={draft.targetId}
                            disabled={pending}
                            inputMode="numeric"
                            onChange={(event) => setField("targetId", event.target.value)}
                        />
                        {showError("targetId") ? <small role="alert">{showError("targetId")}</small> : null}
                    </label>
                ) : null}

                {definition?.target === "role" ? (
                    <label className="admin-field">
                        <span>{t("sections.referralPolicies.p13")}</span>
                        <input
                            value={draft.role}
                            disabled={pending}
                            onChange={(event) => setField("role", event.target.value)}
                        />
                        {showError("role") ? <small role="alert">{showError("role")}</small> : null}
                    </label>
                ) : null}

                <label className="admin-field">
                    <span>{t("sections.referralPolicies.p14")}</span>
                    <input
                        type="date"
                        value={draft.effectiveFrom}
                        disabled={pending}
                        onChange={(event) => setField("effectiveFrom", event.target.value)}
                    />
                    {showError("effectiveFrom") ? <small role="alert">{showError("effectiveFrom")}</small> : null}
                </label>

                <label className="admin-field">
                    <span>{t("sections.referralPolicies.p15")}</span>
                    <select
                        aria-label={t("sections.referralPolicies.p15")}
                        value={draft.roundingMode}
                        disabled={pending}
                        onChange={(event) => setField("roundingMode", event.target.value)}
                    >
                        {ROUNDING_MODES.map((mode) => (
                            <option key={mode} value={mode}>{ROUNDING_LABEL[mode] || mode}</option>
                        ))}
                    </select>
                </label>
            </div>

            <fieldset className="referral-policy-levels">
                <legend>{t("sections.referralPolicies.p16")}</legend>
                <p className="admin-card-note">
                    {t("sections.referralPolicies.p17")}
                </p>
                {POLICY_LEVELS.map((level) => {
                    const rule = draft.rules.find((item) => item.level === level);
                    return (
                        <div className="referral-policy-level" key={level}>
                            <span>{t("sections.referralPolicies.p18")} {level}</span>
                            <input
                                aria-label={t("sections.referralPolicies.p19", {v0: level})}
                                value={rule.percent}
                                disabled={pending}
                                inputMode="decimal"
                                onChange={(event) => setRule(level, {percent: event.target.value})}
                            />
                            <label className="referral-policy-toggle">
                                <input
                                    type="checkbox"
                                    aria-label={t("sections.referralPolicies.p20", {v0: level})}
                                    checked={rule.enabled}
                                    disabled={pending}
                                    onChange={(event) => setRule(level, {enabled: event.target.checked})}
                                />
                                <span>{t("sections.referralPolicies.p21")}</span>
                            </label>
                        </div>
                    );
                })}
                {showError("rules") ? <small role="alert">{showError("rules")}</small> : null}
            </fieldset>

            <div className="referral-policy-actions">
                <button type="submit" className="admin-button admin-button-primary" disabled={pending}>
                    {t("sections.referralPolicies.p22")}
                </button>
                <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={onCancel}>
                    {t("sections.referralPolicies.p23")}
                </button>
            </div>
        </form>
    );
};

const versionColumns = ({canActivateVersions, pending, onActivate}) => [
    {key: "version", label: i18n.t("sections.referralPolicies.p24"), render: (row) => `v${row.version}`},
    {
        key: "status",
        label: i18n.t("sections.referralPolicies.p25"),
        render: (row) => (
            <StatusBadge tone={STATUS_TONE[row.status] || "neutral"}>
                {STATUS_LABEL[row.status] || row.status}
            </StatusBadge>
        ),
    },
    {key: "effective_from", label: i18n.t("sections.referralPolicies.p14"), render: (row) => row.effective_from || "—"},
    {key: "effective_to", label: i18n.t("sections.referralPolicies.p26"), render: (row) => row.effective_to || "—"},
    {
        key: "rules",
        label: i18n.t("sections.referralPolicies.p16"),
        render: (row) => {
            const rules = Array.isArray(row.rules) ? row.rules : [];
            const enabled = rules.filter((rule) => rule.enabled);
            return enabled.length
                ? enabled.map((rule) => `${rule.level}: ${rule.percent}%`).join(" · ")
                : i18n.t("sections.referralPolicies.p27");
        },
    },
    {
        key: "actions",
        label: i18n.t("sections.referralPolicies.p28"),
        render: (row) => {
            if (!canActivate(row)) return "—";
            if (!canActivateVersions) return i18n.t("sections.referralPolicies.p29");
            return (
                <button
                    type="button"
                    className="admin-button admin-button-secondary"
                    disabled={pending}
                    onClick={() => onActivate(row)}
                >
                    {i18n.t("sections.referralPolicies.p30")}
                </button>
            );
        },
    },
];

export const ReferralPoliciesPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const queryClient = useQueryClient();
    const tenantId = capabilities.tenant?.id;
    const canView = capabilities.hasAction("referral.manage");
    // Второй капабилити ещё не заведён во фронт-контракт: пока его нет в actions,
    // активацию не показываем, чтобы не подставлять оператора под 403.
    const canActivateVersions = capabilities.hasAction("referral.policy.activate");

    const [formOpen, setFormOpen] = useState(false);
    const [formPolicyId, setFormPolicyId] = useState(null);
    const [result, setResult] = useState(null);

    const policiesQuery = useQuery({
        queryKey: referralPolicyKeys.list(tenantId),
        queryFn: () => listReferralPolicies({tenantId}),
        enabled: canView && Boolean(tenantId),
    });

    const groups = useMemo(() => groupPolicyVersions(policiesQuery.data), [policiesQuery.data]);

    const invalidate = () => queryClient.invalidateQueries({queryKey: referralPolicyKeys.all(tenantId)});

    const createMutation = useMutation({
        mutationFn: createReferralPolicyVersion,
        onSuccess: () => {
            setResult({kind: "success", message: t("sections.referralPolicies.p31")});
            setFormOpen(false);
            setFormPolicyId(null);
            invalidate();
        },
        onError: (error) => setResult({kind: "error", message: mapApiError(error).message}),
    });

    const activateMutation = useMutation({
        mutationFn: activateReferralPolicyVersion,
        onSuccess: () => {
            setResult({kind: "success", message: t("sections.referralPolicies.p32")});
            invalidate();
        },
        onError: (error) => setResult({kind: "error", message: mapApiError(error).message}),
    });

    if (!canView) {
        return (
            <DataState
                title={t("sections.referralPolicies.p33")}
                copy={t("sections.referralPolicies.p34")}
            />
        );
    }

    if (!tenantId) {
        return (
            <DataState
                title={t("sections.referralPolicies.p35")}
                copy={t("sections.referralPolicies.p36")}
            />
        );
    }

    const openForm = (policyId = null) => {
        setFormPolicyId(policyId);
        setFormOpen(true);
        setResult(null);
    };

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("sections.referralPolicies.p37")}</h1>
                    <p className="admin-page-copy">
                        {t("sections.referralPolicies.p38")}
                    </p>
                </div>
                <button type="button" className="admin-button admin-button-primary" onClick={() => openForm(null)}>
                    <Plus aria-hidden="true" size={16} /> {t("sections.referralPolicies.p8")}
                </button>
            </header>

            <MutationResultBanner result={result} />

            {formOpen ? (
                <PolicyVersionForm
                    policyId={formPolicyId}
                    tenantId={tenantId}
                    pending={createMutation.isPending}
                    onCancel={() => setFormOpen(false)}
                    onSubmit={(draft) => createMutation.mutate(draft)}
                />
            ) : null}

            {policiesQuery.isLoading ? (
                <DataState title={t("sections.referralPolicies.p39")} copy={t("sections.referralPolicies.p40")} />
            ) : policiesQuery.isError ? (
                <DataState
                    title={t("sections.referralPolicies.p41")}
                    copy={mapApiError(policiesQuery.error).message}
                />
            ) : groups.length === 0 ? (
                <DataState
                    title={t("sections.referralPolicies.p42")}
                    copy={t("sections.referralPolicies.p43")}
                    items={[t("sections.referralPolicies.p44"), t("sections.referralPolicies.p45"), t("sections.referralPolicies.p46")]}
                />
            ) : (
                groups.map((group) => (
                    <section className="admin-card referral-policy-group" key={group.policyId}>
                        <header>
                            <div>
                                <p className="admin-eyebrow">
                                    <Layers aria-hidden="true" size={13} />{" "}
                                    {scopeType(group.scopeType)?.label || group.scopeType || i18n.t("sections.referralPolicies.p47")}
                                </p>
                                <h2>{group.name}</h2>
                                <p className="admin-card-note">
                                    {group.active
                                        ? i18n.t("sections.referralPolicies.p48", {v0: group.active.version})
                                        : i18n.t("sections.referralPolicies.p49")}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="admin-button admin-button-secondary"
                                onClick={() => openForm(group.policyId)}
                            >
                                {i18n.t("sections.referralPolicies.p50")}
                            </button>
                        </header>

                        <AdminTable
                            columns={versionColumns({
                                canActivateVersions,
                                pending: activateMutation.isPending,
                                onActivate: (row) => activateMutation.mutate({versionId: row.id}),
                            })}
                            rows={group.versions}
                            emptyText={i18n.t("sections.referralPolicies.p51")}
                        />
                    </section>
                ))
            )}

            <article className="admin-card">
                <p className="admin-card-title"><Network size={14} /> {t("sections.referralPolicies.p52")}</p>
                <p className="admin-card-note">
                    {t("sections.referralPolicies.p53")}
                </p>
            </article>
        </div>
    );
};
